import { useCallback, useEffect, useRef, useState } from 'react'
import type { AuditEntry, AuditSyncData, BridgeMessage, RestoreBridge } from './bridge'
import { restoreRunsApi } from './api'

/**
 * Handing the daemon's audit records to Orchestrate (the bridge document, revised 13 Oct 2026,
 * section 6). The daemon contacts nothing: its journal reaches Orchestrate only through this page.
 *
 *   - Live: each `auditEntry` frame is saved as it arrives.
 *   - Catch-up, when the page loads and whenever the link to the daemon comes back: `auditSync`,
 *     asked again while the reply says there is more.
 *   - Nothing in the messages names the laptop, so Orchestrate cannot keep "the last sequence
 *     stored" per laptop. The journal is asked for from the start every time and the back end
 *     ignores a record it already holds.
 *   - The journal is numbered 1, 2, 3 with no gaps. A number the catch-up does not return means
 *     records were withheld between the laptop and Orchestrate: it is reported as an incident.
 */
const CHUNK = 500
export interface AuditState { handedOver: number; lastSeq: number | null; journal: boolean | null; missing: number[]; problem: string | null }

export function useAuditJournal(sessionToken: string, bridge: RestoreBridge) {
    const [state, setState] = useState<AuditState>({ handedOver: 0, lastSeq: null, journal: null, missing: [], problem: null })
    const token = useRef(sessionToken)
    token.current = sessionToken
    const running = useRef(false)

    const save = useCallback(async (entries: AuditEntry[]) => {
        for (let i = 0; i < entries.length; i += CHUNK) await restoreRunsApi.audit(entries.slice(i, i + CHUNK))
    }, [])

    const catchUp = useCallback(async () => {
        if (!token.current || running.current) return
        running.current = true
        try {
            const seen = new Set<number>()
            let afterSeq = 0
            let lastSeq = 0
            for (;;) {
                const data = (await bridge.request({ type: 'auditSync', afterSeq }, token.current)).data as AuditSyncData | undefined
                if (!data) return
                // This daemon posts its records elsewhere and has none for Orchestrate.
                if (data.journal === false) { setState((s) => ({ ...s, journal: false, problem: null })); return }
                const entries = (data.entries ?? []).filter((e) => Number.isInteger(e?.seq) && e.seq > 0)
                if (entries.length) await save(entries)
                entries.forEach((e) => seen.add(e.seq))
                const highest = entries.reduce((max, e) => Math.max(max, e.seq), afterSeq)
                lastSeq = Math.max(lastSeq, highest, data.lastSeq ?? 0)
                if (!data.more || highest === afterSeq) break
                afterSeq = highest
            }
            const missing: number[] = []
            for (let seq = 1; seq <= lastSeq; seq += 1) if (!seen.has(seq)) missing.push(seq)
            if (missing.length) await restoreRunsApi.auditGap(missing, lastSeq)
            setState({ handedOver: seen.size, lastSeq, journal: true, missing, problem: null })
        } catch (error) {
            setState((s) => ({ ...s, problem: (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (error as Error).message }))
        } finally { running.current = false }
    }, [bridge, save])

    const onMessage = useCallback((message: BridgeMessage) => {
        if (message.kind === 'restoreLink' && message.state === 'open') { void catchUp(); return }
        if (message.kind !== 'restore' || message.frame.type !== 'auditEntry') return
        const { entry } = message.frame
        if (!Number.isInteger(entry?.seq) || !entry.record) return
        save([entry])
            .then(() => setState((s) => ({ ...s, journal: true, handedOver: s.handedOver + 1, lastSeq: Math.max(s.lastSeq ?? 0, entry.seq) })))
            .catch((error: Error) => setState((s) => ({ ...s, problem: error.message })))
    }, [catchUp, save])
    useEffect(() => bridge.subscribe(onMessage), [bridge, onMessage])

    // On load, once there is a pass.
    const started = useRef(false)
    useEffect(() => {
        if (started.current || !sessionToken) return
        started.current = true
        void catchUp()
    }, [sessionToken, catchUp])

    return { ...state, catchUp }
}
